import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Stack,
  Box,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';

interface UpdateProgressDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { dueDate: string; notes: string }) => void;
  initialData?: {
    dueDate: string;
    notes: string;
  };
}

const UpdateProgressDialog: React.FC<UpdateProgressDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
}) => {
  const [dueDate, setDueDate] = React.useState('');
  const [notes, setNotes] = React.useState('');

  const handleSave = (): void => {
    onSubmit({ dueDate, notes });
  };
  const formatDateForInput = (date?: string | null): string => {
    if (!date) return '';

    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }

      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return '';

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  useEffect(() => {
    if (open && initialData) {
      const formattedDate = formatDateForInput(initialData.dueDate);
      setDueDate(formattedDate);
      setNotes(initialData.notes || '');
    } else if (!open) {
      setDueDate('');
      setNotes('');
    }
  }, [open, initialData]);

  return (
    <Dialog maxWidth="xs" fullWidth open={open} onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6">Update Progress</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2">Due Date</Typography>
            <TextField
              type="date"
              fullWidth
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              size="small"
            />
          </Box>

          <Box>
            <Typography variant="subtitle2">Notes</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              size="small"
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <AppPrimaryButtonOutlined title="Cancel" onClick={onClose} />
        <AppPrimaryButton title="Save" onClick={handleSave} />
      </DialogActions>
    </Dialog>
  );
};

export default UpdateProgressDialog;
